import React, { useCallback, useState } from "react";

import { Button, Col, Row } from "react-bootstrap";

import { useForm } from "react-hook-form";

import {
  ContactFragment,
  ContactInput,
  MfaPreferenceType,
  ParticipantStatus,
  ParticipantTypes,
  useUpdateContactDetailsMutation,
  useUpdateMfaPreferenceMutation,
} from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { ContactDetailsResolver } from "@components/account_profile/account_details/ContactDetailsResolver";
import { FieldFormatter } from "@components/detail_drawer/FieldFormatter";
import { DetailCell } from "@components/resale_submission/DetailCell";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { ContactDetailsForm } from "@pages/register/steps/UserDetails/ContactDetailsForm";
import { formatDateStringLong, formatValueToDisplayFriendly } from "@util/index";

interface ContactDetailsProps {
  contact: ContactFragment;
  participantTypes: (ParticipantTypes | undefined)[];
  isInternalUser: boolean;
  participantId?: number;
  mfaPreference?: MfaPreferenceType;
  accountStatus?: string;
  onUpdate: () => void;
}

const ContactDetailsAccordion: React.FC<ContactDetailsProps> = ({
  contact,
  isInternalUser,
  participantTypes,
  participantId,
  mfaPreference,
  accountStatus,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"info" | "error" | "success">("info");
  const [showToast, setShowToast] = useState(false);
  const [currentContact, setCurrentContact] = useState<ContactFragment>(contact);
  const [updateContactDetailsMutation] = useUpdateContactDetailsMutation();
  const [currentMfaPreference, setCurrentMfaPreference] = useState(mfaPreference);
  const [updateMfaPreferenceMutation] = useUpdateMfaPreferenceMutation();

  const CONTACT_DETAILS_FORMATTERS: FieldFormatter<ContactFragment>[] = [
    { title: "Title", format: (contact: ContactFragment) => contact.title || "-" },
    { title: "First name", format: (contact: ContactFragment) => contact.first_name || "-" },
    { title: "Middle name", format: (contact: ContactFragment) => contact.middle_name || "-" },
    { title: "Last name", format: (contact: ContactFragment) => contact.last_name || "-" },
    { title: "Pronouns", format: (contact: ContactFragment) => contact.pronouns || "-" },
    { title: "Preferred name", format: (contact: ContactFragment) => contact.preferred_name || "-" },
    { title: "Email", format: (contact: ContactFragment) => contact.email || "-" },
    { title: "Mobile number", format: (contact: ContactFragment) => contact.phone_number || "-" },
    { title: "Work number", format: (contact: ContactFragment) => contact.work_number || "-" },
    { title: "Home number", format: (contact: ContactFragment) => contact.home_number || "-" },
    {
      title: "Status",
      format: (contact: ContactFragment) => (contact.status ? formatValueToDisplayFriendly(contact.status) : "-"),
    },
    ...(!currentMfaPreference || isInternalUser
      ? []
      : [
          {
            title: "MFA Preference",
            format: () =>
              currentMfaPreference === MfaPreferenceType.SMS_MFA
                ? "SMS message"
                : currentMfaPreference === MfaPreferenceType.EMAIL_OTP
                  ? "Email message"
                  : "Inactive",
          },
        ]),
  ];

  const {
    control,
    formState: { isValid, errors },
    getValues,
    handleSubmit,
  } = useForm<UserDetailsInput>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: (data) =>
      ContactDetailsResolver(
        data,
        data.contact_details?.status,
        participantTypes?.filter((type): type is ParticipantTypes => type !== undefined),
      ),
    defaultValues: { contact_details: contact, mfa_preference: currentMfaPreference },
  });

  const submitHandler = useCallback(async () => {
    const data = getValues();
    const updatedMfaPreference = data.mfa_preference;

    if (!participantId) {
      setToastMessage("Participant ID is missing, cannot update contact details.");
      setToastVariant("error");
      setShowToast(true);
      return;
    }

    try {
      const response = await updateContactDetailsMutation({
        variables: {
          participantId,
          contactInput: cleanInput(data.contact_details),
        },
      });

      if (updatedMfaPreference !== currentMfaPreference) {
        const mfaResponse = await updateMfaPreferenceMutation({
          variables: { mfaMethod: updatedMfaPreference },
        });

        if (!mfaResponse.data?.updateMfaPreference?.success) {
          throw new Error(mfaResponse.data?.updateMfaPreference?.message || "Failed to update MFA preference.");
        }

        setCurrentMfaPreference(updatedMfaPreference);
      }

      const updateResult = response.data?.updateContactDetails;
      if (updateResult?.success) {
        setCurrentContact(updateResult.contact as ContactFragment);
        setIsEditing(false);
        setToastMessage(updateResult.message || "Contact details updated successfully!");
        setToastVariant("success");
        onUpdate();
      } else {
        setToastMessage(updateResult?.message || "Failed to update contact details");
        setToastVariant("error");
      }
    } catch (error) {
      console.error(error);
      setToastMessage("An error occurred while updating contact details");
      setToastVariant("error");
    } finally {
      setShowToast(true);
    }
  }, [
    getValues,
    participantId,
    updateContactDetailsMutation,
    updateMfaPreferenceMutation,
    currentMfaPreference,
    onUpdate,
  ]);

  const shouldShow =
    !isInternalUser ||
    (isInternalUser &&
      (participantTypes.includes(ParticipantTypes.ARTIST) || participantTypes.includes(ParticipantTypes.BENEFICIARY)));

  if (!shouldShow) {
    return null;
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  // Conditionally render Start Date and End Date based on internal/external status
  const renderStartAndEndDate = () => {
    if (isInternalUser) {
      return null;
    }

    const statusIsInactive = currentContact.status && currentContact.status !== ParticipantStatus.ACTIVE;
    return (
      <>
        <Col md={6}>
          <DetailCell
            title="Start date"
            value={currentContact.date_created ? formatDateStringLong(currentContact.date_created) : "-"}
          />
        </Col>
        {statusIsInactive && (
          <Col md={6}>
            <DetailCell
              title="End date"
              value={currentContact.date_modified ? formatDateStringLong(currentContact.date_modified) : "-"}
            />
          </Col>
        )}
      </>
    );
  };

  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type={toastVariant} />
      <CustomAccordion
        accordionKey="ContactDetails"
        contrastOpenStyle={false}
        header={<h5 className="mb-0">My contact details</h5>}
        defaultActiveKey="ContactDetails"
        customHeaderStyle="py-4"
        preventCloseOnContentClick
        showBottomLine
      >
        <Row>
          {isEditing ? (
            <>
              <ContactDetailsForm
                control={control}
                errors={errors}
                handleCheckboxChange={() => {}}
                isBeneficiary={participantTypes.includes(ParticipantTypes.BENEFICIARY)}
                isArtist={participantTypes.includes(ParticipantTypes.ARTIST)}
                isEditing={isEditing}
                mfaPreference={currentMfaPreference}
                userStatus={accountStatus}
              />
              <Col className="d-flex justify-content-end">
                <Button variant="secondary" onClick={handleCancel} className="me-2">
                  Cancel
                </Button>
                <Button variant="primary" disabled={!isValid} onClick={handleSubmit(submitHandler)}>
                  Save
                </Button>
              </Col>
            </>
          ) : (
            <>
              <Col md={11}>
                <Row className="gap-4">
                  {CONTACT_DETAILS_FORMATTERS.map(({ title, format }) => (
                    <Col md={5} className="d-flex flex-column" key={title}>
                      <DetailCell title={title} value={format(currentContact)} />
                    </Col>
                  ))}
                  {renderStartAndEndDate()}
                </Row>
              </Col>
              <Col md={1} className="d-flex justify-content-end">
                <IconLink to="#" src={PenWriteEditIcon} text="Edit" className="me-2" onClick={handleEditToggle} />
              </Col>
            </>
          )}
        </Row>
      </CustomAccordion>
    </>
  );
};

const cleanInput = (input: Partial<ContactFragment>): ContactInput => {
  return {
    title: input.title,
    first_name: input.first_name || "",
    middle_name: input.middle_name,
    last_name: input.last_name || "",
    preferred_name: input.preferred_name || "",
    pronouns: input.pronouns,
    email: input.email,
    phone_number: input.phone_number,
    work_number: input.work_number,
    home_number: input.home_number,
  };
};

export default ContactDetailsAccordion;
